import type React from 'react';
import createElement from 'react-syntax-highlighter/dist/esm/create-element.js';
import PrismLight from 'react-syntax-highlighter/dist/esm/prism-light.js';
import yaml from 'refractor/yaml';
import cedar from './prismCedar';
import { CodeLine, type CodeLineDecoration } from './codeBlockLine';
import { CODE_FONT } from './codeBlockStyles';

// Registered on first render rather than at import. A bare top-level call is an import-time
// side effect, which `sideEffects: false` in package.json tells a bundler it may drop; the
// same reason the font faces go in through ensureInterFontFace and not a top-level call.
let languagesRegistered = false;

const ensureLanguagesRegistered = (): void => {
    if (languagesRegistered) {
        return;
    }
    languagesRegistered = true;
    PrismLight.registerLanguage('yaml', yaml);
    PrismLight.registerLanguage('cedar', cedar);
};

// Prism token colors bound to the codeblock theme tokens so highlighting follows the theme.
const PRISM_MALACHITE_THEME: { [key: string]: React.CSSProperties } = {
    'code[class*="language-"]': CODE_FONT,
    'pre[class*="language-"]': CODE_FONT,
    comment: { color: 'var(--malachite-codeblock-comment)' },
    prolog: { color: 'var(--malachite-codeblock-comment)' },
    cdata: { color: 'var(--malachite-codeblock-comment)' },
    punctuation: { color: 'var(--malachite-codeblock-foreground)' },
    key: { color: 'var(--malachite-codeblock-keyword)' },
    atrule: { color: 'var(--malachite-codeblock-keyword)' },
    'attr-name': { color: 'var(--malachite-codeblock-keyword)' },
    directive: { color: 'var(--malachite-codeblock-keyword)' },
    keyword: { color: 'var(--malachite-codeblock-keyword)' },
    property: { color: 'var(--malachite-codeblock-keyword)' },
    boolean: { color: 'var(--malachite-codeblock-keyword)' },
    important: { color: 'var(--malachite-codeblock-keyword)' },
    null: { color: 'var(--malachite-codeblock-keyword)' },
    tag: { color: 'var(--malachite-codeblock-keyword)' },
    anchor: { color: 'var(--malachite-codeblock-keyword)' },
    alias: { color: 'var(--malachite-codeblock-keyword)' },
    // Cedar's entity paths (`StackOne::Action`) are types, which is what this token is for.
    'class-name': { color: 'var(--malachite-codeblock-type)' },
    // The scope variables. Given the one remaining distinct hue so they read as language
    // rather than as another blue keyword.
    builtin: { color: 'var(--malachite-codeblock-number)' },
    operator: { color: 'var(--malachite-codeblock-line-number)' },
    string: { color: 'var(--malachite-codeblock-string)' },
    scalar: { color: 'var(--malachite-codeblock-string)' },
    char: { color: 'var(--malachite-codeblock-string)' },
    url: { color: 'var(--malachite-codeblock-string)' },
    number: { color: 'var(--malachite-codeblock-number)' },
    datetime: { color: 'var(--malachite-codeblock-number)' },
};

type SyntaxNode = {
    type: 'text' | 'element';
    value?: string | number;
    children?: SyntaxNode[];
};

/** Local, not the ambient `rendererProps`: that global leaks out of DefinitelyTyped by accident. */
type RowRendererProps = {
    rows: SyntaxNode[];
    stylesheet: { [key: string]: React.CSSProperties };
    useInlineStyles: boolean;
};

/** Prism keeps the source newline on each row's last token; `CodeLine` supplies the break now. */
const withoutTrailingNewline = <T extends SyntaxNode>(node: T): T => {
    if (node.type === 'text') {
        return typeof node.value === 'string' && node.value.endsWith('\n')
            ? { ...node, value: node.value.slice(0, -1) }
            : node;
    }
    const children = node.children;
    if (!children?.length) {
        return node;
    }
    const last = children[children.length - 1];
    const trimmed = withoutTrailingNewline(last);
    return trimmed === last ? node : { ...node, children: [...children.slice(0, -1), trimmed] };
};

export interface CodeBlockHighlighterProps {
    code: string;
    language: 'yaml' | 'cedar';
    showLineNumbers?: boolean;
    wrapLines?: boolean;
    /** Right padding reserving space for the floating copy button. */
    copyClearance?: boolean;
    /**
     * Per-line markup, by index into `code`. How a diff tints its rows and numbers them from
     * the text each line actually came from.
     */
    decorate?: (index: number) => CodeLineDecoration | undefined;
}

const CodeBlockHighlighter: React.FC<CodeBlockHighlighterProps> = ({
    code,
    language,
    showLineNumbers = true,
    wrapLines = false,
    copyClearance = false,
    decorate,
}) => {
    ensureLanguagesRegistered();

    const lines = code.split('\n');

    // We lay rows out ourselves rather than passing `showLineNumbers`/`wrapLongLines` down:
    // that mode makes every line a flex container, so the Prism token spans become flex items
    // and the line shreds into out-of-order fragments.
    const renderRows = ({ rows, stylesheet, useInlineStyles }: RowRendererProps) =>
        rows.map((row, index) => {
            const decoration = decorate?.(index);
            // A decoration's own number wins: in a diff the row's position in the merged
            // text is not its line number in either of the texts it came from.
            const lineNumber = decoration?.lineNumber ?? (showLineNumbers ? index + 1 : undefined);

            return (
            <CodeLine
                key={index}
                line={lines[index] ?? ''}
                lineNumber={lineNumber}
                wrap={wrapLines}
                decoration={decoration}
            >
                {createElement({
                    node: withoutTrailingNewline(row),
                    stylesheet,
                    useInlineStyles,
                    key: `code-line-${index}`,
                })}
            </CodeLine>
            );
        });

    return (
        <PrismLight
            language={language}
            style={PRISM_MALACHITE_THEME}
            wrapLines
            renderer={renderRows}
            customStyle={{
                ...CODE_FONT,
                margin: 0,
                padding: '16px',
                paddingRight: copyClearance ? '44px' : '16px',
                overflow: 'auto',
            }}
            // The `code` element is the rows' wrapper here, so it takes the sizing the plain
            // path puts on a div of its own.
            codeTagProps={{
                style: { ...CODE_FONT, display: 'block', width: 'max-content', minWidth: '100%' },
            }}
        >
            {code}
        </PrismLight>
    );
};

export default CodeBlockHighlighter;
